<script setup lang="ts">
    import { computed, toRef } from 'vue'

    import { CreatePortfolioEventPayload } from '@Quest/Portfolios/Components/PortfolioCreatorDialog'
    import type { PortfolioCreatorDialogViewModel } from '@Quest/Portfolios/Components/PortfolioCreatorDialog'
    import type { WorkspaceReadModel } from '@Organizations/Workspaces/readmodels'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: PortfolioCreatorDialogViewModel
    }>()

    const emit = defineEmits<{
        (e: 'create-portfolio', payload: CreatePortfolioEventPayload): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- computed ----------------

    const opened = computed(() => vm.value?.opened ?? false)

    const nameError = computed(() =>
        vm.value.errors?.find(e => e.Context === 'Name')?.Error ?? null
    )

    const workspaces = computed<WorkspaceReadModel[]>(() => vm.value.workspaces)

    // writable computeds (replace getter + setter pairs)

    const workspaceId = computed<number>({
        get: () => vm.value.workspaceId,
        set: v => { vm.value.workspaceId = v }
    })

    const name = computed<string | null>({
        get: () => vm.value.name,
        set: v => { vm.value.name = v }
    })

    const description = computed<string | null>({
        get: () => vm.value.description,
        set: v => { vm.value.description = v }
    })

    // ---------------- actions ----------------

    function handleFormSubmit()
    {
        vm.value.clearValidationErrors()

        emit(
            'create-portfolio',
            new CreatePortfolioEventPayload(
                workspaceId.value,
                name.value,
                description.value
            )
        )
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Create portfolio</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <form id="portfolio-creator-dialog-form" @submit.prevent="handleFormSubmit">
                            <div class="form-group">
                                <label>Workspace:</label>
                                <select v-model="workspaceId" class="form-control">
                                    <option v-for="w in workspaces" :value="w.WorkspaceId">{{w.Name}}</option>
                                </select>
                            </div>
                            <div class="form-group">
                                <label>Portfolio name:</label>
                                <input type="text" v-model.trim="name" class="form-control" required maxlength="100" minlength="2">
                                <div class="error">{{nameError}}</div>
                            </div>
                            <div class="form-group">
                                <label>Description (optional):</label>
                                <textarea v-model.trim="description" class="form-control" />
                            </div>
                        </form>
                    </div>

                    <div class="modal-footer">
                        <button type="button" @click="close" class="btn btn-outline-secondary">Cancel</button>
                        <button type="submit" form="portfolio-creator-dialog-form" class="btn btn-primary">Create</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>